import { useState, useEffect, useCallback } from 'react';

import {
  guestyApi,
  hospitableApi,
  hospitableConnectApi,
  hostawayApi,
  lodgifyApi,
  ownerRezApi,
  turnoApi,
  hostfullyApi,
} from 'api/partners';
import { getMassStoreStatus } from 'api/store_sdk';
import { IntegrationType } from 'api/types/partners';
import _ from 'lodash';

import ownerRezLogo from './images/ownerRezLogo.png';
import stayFiLogo from './images/stayFiLogo.png';
import hospitableLogo from './images/hospitableLogo.svg';
import airbnbLogo from './images/airbnbLogo.png';
import guestyLogo from './images/guestyLogo.svg';
import lodgifyLogo from './images/lodgifyLogo.png';
import hostawayLogo from './images/HostawayLogo.png';
import turnoLogo from './images/turnoLogo.svg';
import hostfullyLogo from './images/hostfullyLogo.svg';

import * as yup from 'yup';
import { EImportStoreActionType } from './constants';

const POLLING_INTERVAL = 3000; // Poll every 3 seconds

// Polling Hook
export const usePollingImportStores = (
  massStoreRequestId: number | null,
  interval: number = POLLING_INTERVAL,
) => {
  const [isTaskReady, setIsTaskReady] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const [createdStores, setCreatedStores] = useState([]);

  const pollTaskStatus = useCallback(async () => {
    try {
      if (massStoreRequestId === null) {
        return;
      }

      const { is_finished, request } = await getMassStoreStatus({ massStoreRequestId });

      if (is_finished) {
        setIsTaskReady(true);
        setCreatedStores(request);
        setIsPolling(false); // Stop polling when task is ready
      }
    } catch (error) {
      setIsPolling(false); // Stop polling on error
    }
  }, [massStoreRequestId]);

  useEffect(() => {
    if (isPolling) {
      const pollingInterval = setInterval(() => {
        pollTaskStatus();
      }, interval);

      return () => clearInterval(pollingInterval);
    }
  }, [pollTaskStatus, isPolling, interval]);

  return { isTaskReady, isPolling, createdStores };
};

export const getIntegrationApi = ({ company }: { company: IntegrationType }) => {
  if (company === IntegrationType.LODGIFY.toLowerCase()) {
    return lodgifyApi;
  } else if (company === IntegrationType.HOSTAWAY.toLowerCase()) {
    return hostawayApi;
  } else if (company === IntegrationType.GUESTY.toLowerCase()) {
    return guestyApi;
  } else if (company === IntegrationType.HOSPITABLE.toLowerCase()) {
    return hospitableApi;
  } else if (company === IntegrationType.OWNERREZ.toLowerCase()) {
    return ownerRezApi;
  } else if (company === IntegrationType.HOSPITABLE_CONNECT.replace(' ', '').toLowerCase()) {
    return hospitableConnectApi;
  } else if (company === IntegrationType.TURNO.toLowerCase()) {
    return turnoApi;
  } else if (company === IntegrationType.HOSTFULLY.toLowerCase()) {
    return hostfullyApi;
  }
};

// TODO: Add validation for storeId
export const validateImportProperties = yup.object({
  listings: yup.array().of(
    yup.object({
      storeId: yup
        .mixed()
        .test('valid-action-type', 'Please select a store.', (value, { parent }) => {
          if (parent.action !== EImportStoreActionType.SYNC) {
            return true;
          }

          if (_.isNil(value)) {
            return false;
          }

          if (value && !_.isNaN(value)) {
            return true;
          }

          return false;
        }),
      storeName: yup
        .string()
        .test('valid-store-name', 'Please enter a name', (value, { parent }) => {
          if (parent.action === EImportStoreActionType.CREATE && (value === '' || _.isNil(value))) {
            return false;
          }

          return true;
        }),
    }),
  ),
});

export const getIntegrationLogo = ({ company }: { company: IntegrationType }): string => {
  switch (company.toLowerCase()) {
    case IntegrationType.OWNERREZ.toLowerCase():
      return ownerRezLogo;
    case IntegrationType.HOSPITABLE.toLowerCase():
      return hospitableLogo;
    case IntegrationType.HOSPITABLE_CONNECT.toLowerCase():
      return airbnbLogo;
    case IntegrationType.GUESTY.toLowerCase():
      return guestyLogo;
    case IntegrationType.LODGIFY.toLowerCase():
      return lodgifyLogo;
    case IntegrationType.HOSTAWAY.toLowerCase():
      return hostawayLogo;
    case IntegrationType.TURNO.toLowerCase():
      return turnoLogo;
    case IntegrationType.STAYFI.toLowerCase():
      return stayFiLogo;
    case IntegrationType.HOSTFULLY.toLowerCase():
      return hostfullyLogo;
    default:
      return '';
  }
};
